import React, { useEffect, useState } from "react";
import "./ProductFormModal.css";
import {
  PRODUCT_STATUS,
  PRODUCT_STATUS_OPTIONS,
  normalizeProductStatusForApi,
} from "../../constants/productStatus";

export interface ProductFormInitialValues {
  name: string;
  price: string | number;
  quantity: string | number;
  description: string;
  productStatus: string;
}

interface ModalProps {
  onClose: () => void;
  onSave: (formData: FormData) => void;
  loading: boolean;
  mode?: "create" | "edit";
  initialValues?: ProductFormInitialValues;
}

const ProductFormModal: React.FC<ModalProps> = ({
  onClose,
  onSave,
  loading,
  mode = "create",
  initialValues,
}) => {
  const isEdit = mode === "edit";

  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState("");
  const [description, setDescription] = useState("");
  const [productStatus, setProductStatus] = useState<string>(
    PRODUCT_STATUS.IN_STOCK
  );
  const [images, setImages] = useState<FileList | null>(null);

  useEffect(() => {
    if (!initialValues) return;
    setName(initialValues.name);
    setPrice(String(initialValues.price));
    setQuantity(String(initialValues.quantity));
    setDescription(initialValues.description || "");
    setProductStatus(
      normalizeProductStatusForApi(initialValues.productStatus) as string
    );
    setImages(null);
  }, [initialValues]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("name", name);
    formData.append("price", price);
    formData.append("quantity", quantity);
    formData.append("description", description);
    formData.append("productStatus", productStatus);

    if (!isEdit && !images?.length) {
      alert("Please add at least one product image (JPG, PNG, or WebP).");
      return;
    }

    if (images?.length) {
      for (let i = 0; i < images.length; i++) {
        formData.append("image", images[i]);
      }
    }

    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="product-form-modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <h2>{isEdit ? "Edit Product" : "Add New Product"}</h2>
          <button type="button" onClick={onClose} className="modal-close-btn">
            ×
          </button>
        </header>

        <form onSubmit={handleSubmit} className="modal-form-content">
          <div className="form-scroll-container">
            <div className="form-group">
              <label>Product Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Price</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Quantity</label>
                <input
                  type="number"
                  min="0"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                value={productStatus}
                onChange={(e) => setProductStatus(e.target.value)}
                required
              >
                {PRODUCT_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                required
              />
            </div>
            <div className="form-group">
              <label>
                Product Images
                {isEdit ? " (optional — leave empty to keep current)" : ""}
              </label>
              <input
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => setImages(e.target.files)}
                required={!isEdit}
                style={{ padding: "6px" }}
              />
            </div>
          </div>

          <footer className="modal-footer">
            <button
              type="button"
              className="cancel-btn"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="save-btn" disabled={loading}>
              {loading ? "Saving..." : isEdit ? "Update Product" : "Save Product"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
};

export default ProductFormModal;
